/**
 * Activity Log — root-only history of every staff tool submission, read live
 * from `staff_audit_log` (every staff mutation is recorded there by the global
 * AuditInterceptor; rows are display-mapped server-side via op-display).
 * Searchable by tool, user, or summary, filterable by tool, exportable to CSV.
 *
 * Upstream routing guard: `<AdminRoleRoute requiredRole="root">` already blocks
 * non-root users. This component does a defensive re-check and renders
 * `<PendingApprovalPage />` if a non-root user somehow reaches it.
 */
import { useMemo, useState } from 'react';
import { Download, ScrollText, Search } from 'lucide-react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@staff-app/ui';
import { useAuthStore } from '@/stores/auth-store';
import { PendingApprovalPage } from '@/pages/PendingApprovalPage';
import { cn } from '@/lib/utils';
import { useActivityLog, type ActivityLogEntry } from '../hooks/useActivityLog';

const ALL_TOOLS = 'all';
const PAGE_SIZE = 50;

function statusClass(status: ActivityLogEntry['status']): string {
  return status === 'Success'
    ? 'bg-success/10 text-success'
    : 'bg-destructive/10 text-destructive';
}

/** Serialize the visible rows to a CSV string (RFC-4180 quoting). */
function toCsv(rows: ActivityLogEntry[]): string {
  const header = ['Time (CST)', 'Tool', 'Submitted By', 'Summary', 'Status'];
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const lines = rows.map((r) =>
    [r.time, r.tool, r.submittedBy, r.summary, r.status].map(escape).join(','),
  );
  return [header.map(escape).join(','), ...lines].join('\r\n');
}

export function ActivityLogPage() {
  const me = useAuthStore((s) => s.adminUser);

  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [tool, setTool] = useState<string>(ALL_TOOLS);
  const [viewing, setViewing] = useState<ActivityLogEntry | null>(null);
  const [page, setPage] = useState(0);

  // Search + tool filter run SERVER-SIDE across all history (offset resets to 0).
  const { data, loading, error } = useActivityLog({
    offset: page * PAGE_SIZE,
    limit: PAGE_SIZE,
    search: search.trim() || undefined,
    tool: tool !== ALL_TOOLS ? tool : undefined,
  });
  const entries = useMemo(() => data?.staffActivityLog?.items ?? [], [data]);
  const total = data?.staffActivityLog?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // Full tool list (server-provided), independent of the current page.
  const tools = data?.staffActivityLog?.tools ?? [];

  // Defensive: a non-root user reaching this page (e.g. direct URL) gets the
  // pending-approval view instead of the log.
  if (!me?.roles?.includes('root')) {
    return <PendingApprovalPage />;
  }

  const runSearch = () => {
    setSearch(query);
    setPage(0);
  };
  const changeTool = (t: string) => {
    setTool(t);
    setPage(0);
  };

  const downloadCsv = () => {
    const blob = new Blob([toCsv(entries)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'activity-log.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-4 p-6" data-testid="activity-log-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <ScrollText className="h-6 w-6" />
          {'Activity Log'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {'View all tool submission logs across the entire suite'}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-[280px] flex-1 items-center gap-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') runSearch();
            }}
            placeholder="Search by tool, user, or summary..."
            data-testid="activity-log-search"
          />
          <Button onClick={runSearch} className="cursor-pointer" data-testid="activity-log-search-button">
            <Search className="mr-2 h-4 w-4" />
            {'Search'}
          </Button>
        </div>

        <Select value={tool} onValueChange={changeTool}>
          <SelectTrigger className="w-[220px]" data-testid="activity-log-tool-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_TOOLS}>{'All Tools'}</SelectItem>
            {tools.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          onClick={downloadCsv}
          className="cursor-pointer"
          data-testid="activity-log-download-csv"
        >
          <Download className="mr-2 h-4 w-4" />
          {'Download CSV'}
        </Button>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{'Time (CST)'}</TableHead>
              <TableHead>{'Tool'}</TableHead>
              <TableHead>{'Submitted By'}</TableHead>
              <TableHead>{'Summary'}</TableHead>
              <TableHead>{'Status'}</TableHead>
              <TableHead className="text-right">{'Actions'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {error ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-sm text-destructive">
                  {error.message}
                </TableCell>
              </TableRow>
            ) : loading && entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-sm text-muted-foreground">
                  {'Loading activity…'}
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-sm text-muted-foreground">
                  {search.trim() || tool !== ALL_TOOLS
                    ? 'No activity matches your search.'
                    : 'No activity recorded yet.'}
                </TableCell>
              </TableRow>
            ) : (
              entries.map((e) => (
                <TableRow key={e.id} data-testid={`activity-log-row-${e.id}`}>
                  <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                    {e.time}
                  </TableCell>
                  <TableCell className="font-medium">{e.tool}</TableCell>
                  <TableCell className="text-muted-foreground">{e.submittedBy}</TableCell>
                  <TableCell className="max-w-[360px] truncate" title={e.summary}>
                    {e.summary}
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                        statusClass(e.status),
                      )}
                    >
                      {e.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setViewing(e)}
                      className="cursor-pointer"
                      data-testid={`activity-log-view-${e.id}`}
                    >
                      {'View'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination — page through the entire history (server-side). */}
      <div className="flex items-center justify-between" data-testid="activity-log-pagination">
        <span className="text-xs text-muted-foreground">
          {total > 0
            ? `Showing ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`
            : 'No entries'}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="cursor-pointer"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
            data-testid="activity-log-prev"
          >
            {'← Prev'}
          </Button>
          <span className="text-xs text-muted-foreground" data-testid="activity-log-page-indicator">
            {`Page ${page + 1} / ${totalPages}`}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="cursor-pointer"
            onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
            disabled={page + 1 >= totalPages || loading}
            data-testid="activity-log-next"
          >
            {'Next →'}
          </Button>
        </div>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent data-testid="activity-log-detail">
          <DialogHeader>
            <DialogTitle>{viewing?.tool}</DialogTitle>
            <DialogDescription>{viewing?.time}</DialogDescription>
          </DialogHeader>
          {viewing ? (
            <dl className="flex flex-col gap-3 text-sm">
              <div className="flex flex-col gap-0.5">
                <dt className="text-xs font-medium text-muted-foreground">{'Submitted By'}</dt>
                <dd className="text-foreground">{viewing.submittedBy}</dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-xs font-medium text-muted-foreground">{'Summary'}</dt>
                <dd className="text-foreground">{viewing.summary}</dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-xs font-medium text-muted-foreground">{'Status'}</dt>
                <dd>
                  <span
                    className={cn(
                      'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                      statusClass(viewing.status),
                    )}
                  >
                    {viewing.status}
                  </span>
                </dd>
              </div>
              {viewing.detail ? (
                <div className="flex flex-col gap-0.5">
                  <dt className="text-xs font-medium text-muted-foreground">{'Detail'}</dt>
                  <dd className="text-foreground">{viewing.detail}</dd>
                </div>
              ) : null}
            </dl>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
